/**
 * Registry of LIVE CLASS PROVIDERS (SOLID, Design B).
 *
 * Multi-provider: embedded Jitsi / Zoom / Meet / custom link. Each provider
 * self-describes with a `MeetingProviderManifest` (capabilities + config
 * schema) and knows how to build its `JoinDescriptor` (embed or external) from
 * the session + the config resolved by scope + the user.
 *
 * The registry is the SAME extensibility piece as `learning-blocks/registry`:
 * adding a provider = 1 file + `register()`; nothing else in the app changes.
 *
 * `meeting_config` is SERVER-ONLY (never in sync or in client DTOs): these
 * pieces are PURE and receive the already-resolved config as an argument. The
 * time window and `/join` authorization live on the server (F3-G).
 */

import type { FeatureManifest } from "../academy/country/packs";

/** Default Jitsi domain (mirrors `DEFAULT_JITSI_DOMAIN` in Rust config). */
export const DEFAULT_JITSI_DOMAIN = "meet.jit.si";

/**
 * A field of a provider's config schema. The generic settings UI is generated
 * from `configSchema: SettingsField[]`. `secret` fields (x-secret) render as
 * password and are server-only.
 */
export interface SettingsField {
  /** Stable key of the field within the config. */
  key: string;
  /** Readable label (i18n key or text). */
  label: string;
  /** Control type. */
  type: "text" | "url" | "password" | "boolean" | "number" | "select";
  /** Required for the config to be valid? */
  required?: boolean;
  /** Placeholder / example. */
  placeholder?: string;
  /** Contextual help. */
  help?: string;
  /** Options (only for `type: "select"`). */
  options?: readonly { value: string; label: string }[];
  /** Server-only sealed secret (GET returns `{set:true}`). */
  secret?: boolean;
  /** Default value. */
  default?: string | number | boolean;
}

/** Capabilities declared by a provider (feed the catalog and the UI). */
export interface MeetingProviderCapabilities {
  /** Can it be embedded in an iframe within the app? (Jitsi can). */
  embeddable: boolean;
  /** Does it notify attendance via callback/webhook? */
  attendanceCallback: boolean;
  /** Does it allow fetching the recording automatically? */
  recordingFetch: boolean;
  /** Does it require credentials/config (API key, etc.) to work? */
  requiresCredentials: boolean;
}

/** Self-describing manifest of a live class provider. */
export interface MeetingProviderManifest {
  /** Stable provider id (e.g. "jitsi"). Catalog namespace: `media.provider.<id>`. */
  id: string;
  /** Readable name. */
  name: string;
  /** One-line description. */
  description: string;
  /** Declared capabilities. */
  capabilities: MeetingProviderCapabilities;
  /** Config schema (generic UI). */
  configSchema: readonly SettingsField[];
}

/** Live session (minimal subset a provider needs for the join). */
export interface LiveSession {
  /** Id of the `class_session`. */
  id: string;
  /** Session title (to show in the embedded client). */
  title?: string | null;
  /** Meeting link (external providers: Zoom/Meet/custom). */
  meetingUrl?: string | null;
  /**
   * Salt provided by the SERVER to derive the deterministic room name
   * (Jitsi). Never generated client-side: guarantees that teacher and
   * students land in the same room without exposing a guessable secret.
   */
  roomSalt?: string | null;
}

/** User who is joining (for displayName / role). */
export interface JoinUser {
  id: string;
  name?: string | null;
  email?: string | null;
  /** Role in the session (e.g. "teacher" | "student") for moderation. */
  role?: string | null;
}

/** Descriptor of how to join: embedded (iframe) or external (open link). */
export type JoinDescriptor =
  | {
      mode: "embed";
      provider: string;
      /** Server domain (Jitsi). */
      domain: string;
      /** Deterministic room name. */
      roomName: string;
      /** Participant display name. */
      displayName?: string;
    }
  | {
      mode: "external";
      provider: string;
      /** Validated https URL to open in an external tab/app. */
      url: string;
    };

/**
 * A pluggable live class provider. `buildJoin` is PURE (config injected) so it
 * can be tested without network access. `Embed` is the lazy-loaded iframe
 * component (only `embeddable` providers).
 */
export interface MeetingProvider {
  readonly manifest: MeetingProviderManifest;
  /** Builds the join descriptor. Pure and deterministic. */
  buildJoin(session: LiveSession, cfg: Record<string, unknown>, user: JoinUser): JoinDescriptor;
  /** Lazy load of the embedded component (optional). */
  Embed?: () => Promise<unknown>;
}

/**
 * Registry of live class providers. Mirrors `BlockRegistry`:
 * `register`/`get`/`types`, and throws on a duplicate key (does not overwrite providers).
 */
export class MeetingProviderRegistry {
  private readonly providers = new Map<string, MeetingProvider>();

  /** Registers a provider. Throws if the `id` already exists. */
  register(provider: MeetingProvider): this {
    const { id } = provider.manifest;
    if (this.providers.has(id)) {
      throw new Error(`MeetingProviderRegistry: proveedor duplicado "${id}"`);
    }
    this.providers.set(id, provider);
    return this;
  }

  /** Registers several at once. */
  registerAll(providers: readonly MeetingProvider[]): this {
    for (const p of providers) {
      this.register(p);
    }
    return this;
  }

  get(id: string): MeetingProvider | undefined {
    return this.providers.get(id);
  }

  has(id: string): boolean {
    return this.providers.has(id);
  }

  /** Ids of registered providers (for the settings selector). */
  types(): string[] {
    return [...this.providers.keys()];
  }

  /** All registered providers. */
  all(): MeetingProvider[] {
    return [...this.providers.values()];
  }
}

/** Catalog key of a provider: `media.provider.<id>`. */
export function meetingProviderKey(id: string): string {
  return `media.provider.${id}`;
}

/** Derives the `FeatureManifest` (catalog shape) from a provider manifest. */
export function meetingProviderToFeatureManifest(m: MeetingProviderManifest): FeatureManifest {
  return {
    key: meetingProviderKey(m.id),
    name: m.name,
    description: m.description,
    scope: "org",
    status: "stable",
    // FLAGS: joining a live class gates the RESOLVED provider
    // (dynamic gate by `media.provider.` prefix in handlers/live.rs).
    gateable: true,
    since: "2026-06",
    docsSlug: "profesor",
    implications: {
      navHidden: [`«${m.name}» en los selectores de proveedor (curso/cohorte/sesión)`],
      flowsBlocked: ["Unirse a clases en vivo que resuelvan a este proveedor"],
      dataPolicy: "preserved",
    },
  };
}

// ─── Provider helpers ───────────────────────────────────────────────────

/**
 * Deterministic hash (32-bit FNV-1a, hex) of `${sessionId}:${salt}`. PURE and
 * testable. NOT cryptographic: it only makes the room name unguessable from
 * the server-side salt (which is indeed secret).
 */
export function roomHash(sessionId: string, salt: string): string {
  const input = `${sessionId}:${salt}`;
  let hash = 0x81_1c_9d_c5; // FNV-1a 32-bit offset basis
  for (let i = 0; i < input.length; i++) {
    hash ^= input.codePointAt(i) ?? 0;
    // FNV prime 16777619, in 32-bit arithmetic (Math.imul avoids overflow).
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  // >>> 0 → unsigned 32-bit integer; padded to 8 hex digits.
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/**
 * DETERMINISTIC Jitsi room name: `spaceacademy-{sessionId}-{hash}`. The
 * `salt` is provided by the server (secret): two clients with the same salt
 * land in the same room; without the salt it cannot be guessed. Pure (testable).
 */
export function jitsiRoomName(sessionId: string, salt: string): string {
  return `spaceacademy-${sessionId}-${roomHash(sessionId, salt)}`;
}

/**
 * Validates and normalizes an https URL. Throws `Error` if it isn't a valid
 * http(s) URL. External providers (Zoom/Meet/custom) require https.
 */
export function validateHttpsUrl(raw: string | null | undefined): string {
  if (!raw || typeof raw !== "string") {
    throw new Error("meeting: meeting URL required");
  }
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    throw new Error(`meeting: invalid URL "${raw}"`);
  }
  if (url.protocol !== "https:") {
    throw new Error(`meeting: URL must be https (was "${url.protocol}")`);
  }
  return url.toString();
}
